import React from 'react';
import { motion } from 'framer-motion';
import { Users, Wallet, CheckCircle, Building2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const Investors = ({ onOpenContact }) => {
  const { t } = useLanguage();

  const benefits = [
    t('investors.benefits.item1'),
    t('investors.benefits.item2'),
    t('investors.benefits.item3'),
    t('investors.benefits.item4'),
    t('investors.benefits.item5'),
    t('investors.benefits.item6'),
  ];

  return (
    <section 
      id="investors"
      data-testid="investors-section"
      className="py-20 md:py-28 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header - Tightened */}
        <div className="text-center mb-16 md:mb-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#1A3A4A]/50 mb-4"
          >
            {t('investors.subtitle')}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            data-testid="investors-title"
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A3A4A] tracking-tight mb-6"
          >
            {t('investors.title')}<br />
            <span className="text-[#60B5AF]">{t('investors.titleAccent')}</span>
          </motion.h2>
          
          {/* Single punchy intro line */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="font-body text-lg text-[#1A3A4A]/70 max-w-3xl mx-auto leading-relaxed"
          >
            {t('investors.punchyIntro')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {/* Left Column - Investor Types & Ticket Sizes */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6 flex flex-col"
          >
            {/* Investor Types Card - Enhanced */}
            <Card 
              data-testid="investor-types-card"
              className="bg-white border-l-4 border-l-[#60B5AF] border-t border-r border-b border-[#E5E7EB] shadow-md rounded-xl flex-1"
            >
              <CardContent className="p-8 lg:p-10">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#60B5AF]/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#60B5AF]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A3A4A]">
                    {t('investors.types')}
                  </h3>
                </div>
                <p className="font-body text-sm text-[#1A3A4A]/60 mb-5">
                  {t('investors.typesIntro')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(t('investors.typesList') || ['Angel Investors', 'Family Offices', 'Venture Capital', 'Private Equity', 'Strategic Partners']).map((type) => (
                    <span 
                      key={type}
                      className="px-4 py-2.5 bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg font-body text-sm text-[#1A3A4A] hover:border-[#60B5AF] hover:bg-[#60B5AF]/5 transition-colors cursor-default"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Typical Commitment Card - Enhanced */}
            <Card 
              data-testid="ticket-sizes-card"
              className="bg-white border-l-4 border-l-[#60B5AF] border-t border-r border-b border-[#E5E7EB] shadow-md rounded-xl flex-1"
            >
              <CardContent className="p-8 lg:p-10">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#60B5AF]/10 flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-[#60B5AF]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A3A4A]">
                    {t('investors.ticket')}
                  </h3>
                </div>
                <p className="font-heading text-4xl lg:text-5xl font-bold text-[#60B5AF] mb-3">
                  {t('investors.ticketRange')}
                </p>
                <p className="font-body text-sm text-[#1A3A4A]/60 mb-2">
                  {t('investors.ticketSubtitle')}
                </p>
                <p className="font-body text-sm text-[#1A3A4A]/50">
                  {t('investors.ticketTransparency')}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column - Benefits - Premium Dark Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex"
          >
            <Card 
              data-testid="investor-benefits-card"
              className="bg-[#1A3A4A] border-0 border-t-4 border-t-[#60B5AF] shadow-xl rounded-xl w-full"
            >
              <CardContent className="p-8 lg:p-10 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-[#60B5AF]/20 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-[#60B5AF]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">
                    {t('investors.benefits.title')}
                  </h3>
                </div>
                <ul className="space-y-5 flex-grow">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-[#60B5AF] flex-shrink-0 mt-0.5" />
                      <span className="font-body text-white/80 leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA Button - Enhanced */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="font-body text-sm text-white/50 mb-4 text-center">
                    {t('investors.accessNote')}
                  </p>
                  <Button
                    onClick={onOpenContact}
                    className="w-full bg-[#60B5AF] text-white hover:bg-[#60B5AF]/90 font-body font-semibold rounded-xl py-6 text-base group transition-all"
                    data-testid="investor-cta-btn"
                  >
                    {t('investors.cta')}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Investors;
